"use client";

import { useEffect, useState } from "react";
import { useIsSignedIn } from "@/components/auth/useIsSignedIn";
import {
  boxCapacityForLength,
  effectiveDefaultLength,
  pricePerStem as pricePerStemAt,
  smallestAvailableBox,
  stemsInBox,
} from "@/lib/catalog/pricing";
import type { Variety } from "@/lib/catalog/types";

export const LENGTH_PREFERENCE_KEY = "mag-portal-length-preference";

// Apply per-color price override (Delphinium et al.). Returns the
// effective per-stem price for the active length.
function resolveStemPrice(
  variety: Variety,
  length: number,
  colorChoice?: string,
): number {
  const fallback = pricePerStemAt(variety, length) ?? 0;
  if (!colorChoice || !variety.priceByColor) return fallback;
  if (colorChoice === "mix") {
    const all = Object.values(variety.priceByColor);
    if (all.length === 0) return fallback;
    return Math.min(...all);
  }
  return variety.priceByColor[colorChoice] ?? fallback;
}

// Step 6.6 simplified Magazine card packing caption (one line per spec
// section 3a). Examples:
//   Rose                "25 stems / bunch"
//   Hydrangea           "20 stems / EB · 40 / QB" (Select; Premium 35, Mini 60)
//   Select Carnation    "25 stems / bunch · 100 / EB"
//   Spray Carnation     "10 stems / bunch · 100 / EB"
function packingCaption(variety: Variety, length: number): string {
  const cap = boxCapacityForLength(variety, length);
  if (variety.purchaseUnit === "box") {
    if (!cap) return "Sold by box";
    const eb = cap.EB;
    const qb = cap.QB;
    if (eb !== null && qb !== null) return `${eb} stems / EB · ${qb} / QB`;
    if (eb !== null) return `${eb} stems / EB`;
    if (qb !== null) return `${qb} stems / QB`;
    return "Sold by box";
  }
  // Bunch-unit varieties.
  const bunchPart =
    variety.stemsPerBunch !== null
      ? `${variety.stemsPerBunch} stems / bunch`
      : "";
  // Carnations (and any specialty SKU with EB capacity) get an "* 100 / EB"
  // suffix to make the box minimum visible up front.
  const isCarnation =
    variety.slug === "select-carnation" || variety.slug === "spray-carnation";
  if (isCarnation && cap) {
    const code = smallestAvailableBox(cap);
    if (code) {
      const stems = stemsInBox(cap, code);
      if (stems !== null) return `${bunchPart} · ${stems} / ${code}`;
    }
  }
  return bunchPart;
}

export function ProductCardPricing({
  variety,
  signInLabel,
  colorChoice,
}: {
  variety: Variety;
  signInLabel: string;
  colorChoice?: string;
}) {
  const authState = useIsSignedIn();
  const showInteractiveLengthPicker =
    variety.category === "rose" && variety.lengths.length > 1;
  const [length, setLength] = useState<number>(() =>
    effectiveDefaultLength(variety),
  );

  useEffect(() => {
    if (!showInteractiveLengthPicker) return;
    try {
      const saved = window.localStorage.getItem(LENGTH_PREFERENCE_KEY);
      if (saved !== null) {
        const parsed = parseInt(saved, 10);
        if (variety.lengths.includes(parsed)) {
          // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing one-time from localStorage
          setLength(parsed);
        }
      }
    } catch {
      // localStorage unavailable - keep default.
    }
  }, [showInteractiveLengthPicker, variety.lengths]);

  if (authState !== "authed") {
    return (
      <p
        data-testid="product-card-signed-out-msg"
        className="text-sm text-zinc-500"
      >
        {signInLabel}
      </p>
    );
  }

  const stem = resolveStemPrice(variety, length, colorChoice);
  const caption = packingCaption(variety, length);

  function handleLength(l: number, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setLength(l);
    try {
      window.localStorage.setItem(LENGTH_PREFERENCE_KEY, String(l));
    } catch {
      // ignore
    }
  }

  return (
    <div
      data-testid="product-card-price-block"
      data-purchase-unit={variety.purchaseUnit}
    >
      {showInteractiveLengthPicker && (
        <div className="mb-2 flex gap-1.5">
          {variety.lengths.map((l) => {
            const isActive = l === length;
            return (
              <button
                key={l}
                type="button"
                data-testid={`product-card-length-${l}`}
                aria-pressed={isActive}
                onClick={(e) => handleLength(l, e)}
                className={
                  isActive
                    ? "min-h-6 rounded-full bg-brand-teal px-2.5 py-0.5 text-xs font-semibold text-white"
                    : "min-h-6 rounded-full border border-brand-border px-2.5 py-0.5 text-xs text-brand-text-muted hover:border-brand-teal hover:text-brand-text"
                }
              >
                {l} cm
              </button>
            );
          })}
        </div>
      )}
      <p
        data-testid="product-card-price-stem"
        className="text-[22px] font-bold leading-tight tracking-tight text-brand-text"
      >
        ${stem.toFixed(2)}{" "}
        <span className="text-[13px] font-medium text-brand-text-muted">
          / stem
        </span>
      </p>
      {caption && (
        <p
          data-testid="product-card-pack-caption"
          className="mt-0.5 text-[12px] text-brand-text-muted"
        >
          {caption}
        </p>
      )}
    </div>
  );
}
